import { Box, CardMedia, SxProps, Theme } from '@mui/material';
import React, { useCallback, useRef } from 'react';

import { getShowImageAriaLabel } from '../../../constants/marketplace';
import { GALLERY_THUMBNAIL_SIZE, HIDDEN_SCROLLBAR_SX } from '../../../constants/ui';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';

interface IProps {
  images: string[];
  activeImage?: string | null;
  alt: string;
  onPick: (image: string) => void;
}

interface IThumbnailProps {
  image: string;
  alt: string;
  isActive: boolean;
  onPick: (image: string) => void;
}

const railSx: SxProps<Theme> = { display: 'flex', gap: 1, overflowX: 'auto', ...HIDDEN_SCROLLBAR_SX };

const thumbnailSx: SxProps<Theme> = {
  p: 0,
  flexShrink: 0,
  width: GALLERY_THUMBNAIL_SIZE,
  height: GALLERY_THUMBNAIL_SIZE,
  borderRadius: 0.5,
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: 'common.white',
  border: 1,
  borderColor: 'divider',
};

const activeThumbnailSx: SxProps<Theme> = { ...thumbnailSx, borderColor: 'primary.main' };

const thumbnailMediaSx: SxProps<Theme> = { width: '100%', height: '100%', objectFit: 'cover' };

const Thumbnail: React.FC<IThumbnailProps> = ({ image, alt, isActive, onPick }) => {
  const handleClick = useCallback(() => {
    onPick(image);
  }, [onPick, image]);

  return (
    <Box
      component="button"
      type="button"
      aria-label={getShowImageAriaLabel(alt)}
      aria-pressed={isActive}
      onClick={handleClick}
      sx={isActive ? activeThumbnailSx : thumbnailSx}
    >
      <CardMedia component="img" image={image} alt="" sx={thumbnailMediaSx} />
    </Box>
  );
};

export const ThumbnailRail: React.FC<IProps> = ({ images, activeImage, alt, onPick }) => {
  const railRef = useRef<HTMLDivElement>(null);
  const hasRail = images.length > 1;

  useHorizontalWheelScroll(railRef, hasRail);

  if (!hasRail) {
    return null;
  }

  return (
    <Box ref={railRef} sx={railSx}>
      {images.map((image) => (
        <Thumbnail key={image} image={image} alt={alt} isActive={image === activeImage} onPick={onPick} />
      ))}
    </Box>
  );
};
